<script setup lang="ts">
import {
  Percent,
  Landmark,
  Calculator,
  HandCoins,
  CreditCard,
  ChartColumnIncreasing,
  Building2,
  Wallet
} from 'lucide-vue-next'
import MobileSidebarNavItems from './MobileSidebarNavItems.vue'
import MobileSidebarAccentedLink from './MobileSidebarAccentedLink.vue'
import type { IMenuLinks } from './types'

const { getPathWithLocale } = useLocalizedPath()
const localizedCreditsIslamic = computed(() => getPathWithLocale('/credits/islamic'))

const props = defineProps<{ menuLinks: IMenuLinks }>()
</script>

<template>
  <SheetContent side="left">
    <SheetHeader>
      <SheetTitle>
        <Logo class="h-[28px] w-auto" />
      </SheetTitle>
    </SheetHeader>
    <div class="grow overflow-hidden">
      <ScrollArea class="h-full">
        <Accordion type="single" class="w-full" collapsible>
          <MobileSidebarNavItems
            :title="$t('common.banks')"
            slug="banks"
            :links="props.menuLinks"
          >
            <template #header>
              <MobileSidebarAccentedLink
                to="/banks"
                :icon="Building2"
                :title="$t('common.banks_of_kyrgyzstan')"
              />
            </template>
            <template #footer>
              <MobileSidebarAccentedLink
                to="/bank/nbkr"
                :icon="Landmark"
                :title="$t('menu.banks.nbkr')"
              />
            </template>
          </MobileSidebarNavItems>
          <MobileSidebarNavItems
            :title="$t('common.credits')"
            slug="credits"
            :links="props.menuLinks"
          >
            <template #header>
              <MobileSidebarAccentedLink
                to="/credits"
                :icon="Wallet"
                :title="$t('menu.credits.title')"
              />
            </template>
            <template #footer>
              <MobileSidebarAccentedLink
                to="/credits/online-request"
                :icon="Percent"
                :title="$t('menu.credits.online_request')"
              />
            </template>
          </MobileSidebarNavItems>
          <MobileSidebarNavItems
            :title="$t('common.deposits')"
            slug="deposits"
            :links="props.menuLinks"
          >
            <template #header>
              <MobileSidebarAccentedLink
                to="/deposits"
                :icon="HandCoins"
                :title="$t('menu.deposits.title')"
              />
            </template>
            <template #footer>
              <MobileSidebarAccentedLink
                to="/deposits/calculator"
                :icon="Calculator"
                :title="$t('menu.deposits.calculator')"
              />
            </template>
          </MobileSidebarNavItems>
          <MobileSidebarNavItems
            :title="$t('common.cards')"
            slug="cards"
            :links="props.menuLinks"
          >
            <template #header>
              <MobileSidebarAccentedLink
                to="/cards"
                :icon="CreditCard"
                :title="$t('menu.cards.title')"
              />
            </template>
          </MobileSidebarNavItems>
          <SheetClose as-child>
            <NuxtLink :to="localizedCreditsIslamic" class="block w-full py-4 text-sm font-bold">
              {{ $t('menu.triggers.islamic') }}
            </NuxtLink>
          </SheetClose>
          <MobileSidebarNavItems
            :title="$t('menu.triggers.more')"
            slug="other"
            :links="props.menuLinks"
          />
          <MobileSidebarNavItems
            :title="$t('common.exchange_rates')"
            slug="rates"
            :links="props.menuLinks"
          >
            <template #header>
              <MobileSidebarAccentedLink
                to="/rates"
                :icon="ChartColumnIncreasing"
                :title="$t('menu.rates_currencies.title')"
              />
            </template>
          </MobileSidebarNavItems>
          <SheetClose as-child>
            <NuxtLinkLocale
              to="/bank/bakai"
              class="flex w-full items-center gap-2 py-4 font-semibold"
            >
              <NuxtImg
                src="/images/banks/bakai.png"
                width="20"
                height="20"
                :alt="$t('widget.mobile_sidebar.bakai_logo_alt')"
                format="avif,webp"
              />
              {{ $t('common.bakay_bank') }}
            </NuxtLinkLocale>
          </SheetClose>
        </Accordion>
        <ScrollBar />
      </ScrollArea>
    </div>
  </SheetContent>
</template>
